import { memo } from 'react';

import Link from 'next/link';

import Logo, { LIPS_PARTNERED_LOGO } from '@/components/ui/Logo';
import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';
import { ConsultantDoctifyMiniWidget } from '@/components/sections/DoctorCard/ConsultantDoctifyMiniWidget';
import type { CarouselDoctor } from '@/utils/getDoctorsByCategory';

interface Props {
  doctor: CarouselDoctor;
}

const SpecialistCarouselCard = ({ doctor }: Props) => {
  const specialities: string[] = doctor.specialities.length
    ? doctor.specialities
    : doctor.primarySpeciality
      ? [doctor.primarySpeciality]
      : [];

  const visible = specialities.slice(0, 1);
  const remaining = specialities.length - visible.length;

  const avatarPath = doctor.image?.url || `${doctor.slug}.webp`;

  return (
    <Link
      href={`/our-specialists/${doctor.slug}`}
      className="flex h-full w-60 shrink-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm sm:w-65 lg:w-70"
    >
      <div className="bg-card-gradient relative h-55 w-full shrink-0 overflow-hidden">
        <div className="absolute top-3 left-3 z-10">
          <Logo
            src={LIPS_PARTNERED_LOGO}
            alt="LIPS Healthcare"
            width={36}
            height={36}
          />
        </div>
        <R2Image
          path={avatarPath}
          alt={doctor.fullName}
          objectFit="contain"
          className="h-full w-full object-contain object-bottom"
          sizes="(max-width: 640px) 240px, (max-width: 1024px) 260px, 280px"
        />
      </div>

      <div className="flex flex-1 flex-col gap-1 p-4">
        <RichText
          styles={{
            p: 'font-semibold text-base text-text-ink line-clamp-1 mb-0',
          }}
        >
          {doctor.fullName}
        </RichText>

        {visible.length > 0 && (
          <div className="mt-1 flex flex-nowrap items-center gap-1.5 overflow-hidden">
            {visible.map((spec, i) => (
              <RichText
                key={`${spec}-${i}`}
                className="text-theme-brand-blue border-theme-success-2 bg-theme-success-opac min-w-0 truncate rounded-full border px-2 py-1 text-xs font-bold"
                styles={{ p: 'mb-0' }}
              >
                {spec}
              </RichText>
            ))}
            {remaining > 0 && (
              <RichText
                className="text-theme-brand-blue border-theme-success-2 bg-theme-success-opac shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold"
                styles={{ p: 'mb-0' }}
              >
                {`+${remaining}`}
              </RichText>
            )}
          </div>
        )}

        {doctor.doctifySlug && (
          <div className="mt-2">
            <ConsultantDoctifyMiniWidget
              doctifySlug={doctor.doctifySlug}
              widgetId={`carousel-doctify-${doctor.slug}`}
              justify="justify-start"
            />
          </div>
        )}
      </div>
    </Link>
  );
};

export default memo(SpecialistCarouselCard);
